import { ClickAwayListener } from "@mui/material";
import { useSearch } from "@redotech/react-util/search";
import { IFaqArticle } from "@redotech/redo-model/faq/faq-db-parser";
import SearchIcon from "@redotech/redo-web/arbiter-icon/search-sm_filled.svg";
import Fuse from "fuse.js";
import { memo, useMemo, useState } from "react";
import { RedoTextInput } from "../../arbiter-components/input/redo-text-input";
import {
  RedoList,
  RedoListItem,
} from "../../arbiter-components/list/redo-list";
import { Dropdown } from "../../dropdown";
import { useIsMobile } from "../../utils/check-mobile";

export const articleSearcher = new Fuse<IFaqArticle>([], {
  keys: ["title"],
  threshold: 0.2,
});

export type FaqSearchViewProps = {
  articles: IFaqArticle[];
  actions: {
    onSubmitSearch: (searchString: string) => void;
    onSelectSearchItem: (value: IFaqArticle | null) => void;
  };
};
export const FaqSearchView = memo(function FaqSearch({
  articles,
  actions,
}: FaqSearchViewProps) {
  const [articleSearchDropdownRef, setArticleSearchDropdownRef] =
    useState<HTMLElement | null>(null);
  const [articleSearchDropdownOpen, setArticleSearchDropdownOpen] =
    useState(false);
  const [searchString, setSearchString] = useState("");
  const [focusedIndex, setFocusedIndex] = useState<number | undefined>();
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  const articlesBeingShown = useSearch(articleSearcher, articles, searchString);

  function closeDropdown() {
    setArticleSearchDropdownOpen(false);
    setSearchString("");
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && focusedIndex === undefined && searchString) {
      actions.onSubmitSearch(searchString);
      closeDropdown();
    }
  }

  const isMobile = useIsMobile();

  const articleItems = useMemo(() => {
    return articlesBeingShown.map<RedoListItem<IFaqArticle>>((article) => {
      return {
        id: article._id,
        type: "text",
        text: article.title,
        value: article,
      };
    });
  }, [articlesBeingShown]);

  return (
    <div>
      <div
        onClick={() => setArticleSearchDropdownOpen(true)}
        ref={setArticleSearchDropdownRef}
      >
        <div onKeyDown={onKeyDown}>
          <RedoTextInput
            IconLeading={SearchIcon}
            placeholder="Search"
            ref={setInputRef}
            setValue={setSearchString}
            size={isMobile ? "sm" : "md"}
            value={searchString}
          />
        </div>
      </div>
      {articleSearchDropdownOpen && (
        <ClickAwayListener
          onClickAway={() => {
            setArticleSearchDropdownOpen(false);
          }}
        >
          <Dropdown
            anchor={articleSearchDropdownRef}
            fitToAnchor
            open={articleSearchDropdownOpen}
          >
            <RedoList
              focusedIndex={focusedIndex}
              items={articleItems}
              itemSelected={({ item }) => {
                closeDropdown();
                actions.onSelectSearchItem(item.value);
              }}
              refToListenTo={inputRef}
              setFocusedIndex={setFocusedIndex}
              size="sm"
            />
          </Dropdown>
        </ClickAwayListener>
      )}
    </div>
  );
});
